import React from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useUserData } from "../../context/UserDataFetcher";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StoreIcon from "@mui/icons-material/Store";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import GroupsIcon from "@mui/icons-material/Groups";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useIonRouter } from "@ionic/react";
import { useDemoMode } from "../../context/DemoModeProvider";

type NavOption = {
  label: string;
  route: string;
  icon: JSX.Element;
  requiresDemo?: boolean;
  requiresManufacturerAdmin?: boolean;
};

type DashboardSidebarProps = {
  onNavigate?: (route: string) => void;
  width?: number | string;
};

const adminNavOptions: NavOption[] = [
  {
    label: "Home",
    route: "/admin/home",
    icon: <HomeIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Manage Shipments",
    route: "/admin/shipments/manage",
    icon: <ViewInArIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Manage Users",
    route: "/admin/users/manage",
    icon: <PersonIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Manage Dealers",
    route: "/admin/dealers/manage",
    icon: <StoreIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Manage Trucks",
    route: "/admin/trucks/manage",
    icon: <LocalShippingIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "View Shipments",
    route: "/admin/shipments",
    icon: <FormatListBulletedIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "View Orders",
    route: "/admin/orders",
    icon: <AssignmentIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Training Sandbox",
    route: "/admin/scan-sandbox",
    icon: <QrCode2Icon sx={{ fontSize: "2rem" }} />,
    requiresDemo: true,
  },
];

const dealerNavOptions: NavOption[] = [
  {
    label: "Home",
    route: "/dealer/home",
    icon: <HomeIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Manage Users",
    route: "/dealer/users/manage",
    icon: <PersonIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "View Shipments",
    route: "/dealer/shipments",
    icon: <FormatListBulletedIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "View Orders",
    route: "/dealer/orders",
    icon: <AssignmentIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "Training Sandbox",
    route: "/dealer/scan-sandbox",
    icon: <QrCode2Icon sx={{ fontSize: "2rem" }} />,
    requiresDemo: true,
  },
];

const driverNavOptions: NavOption[] = [
  {
    label: "My Shipments",
    route: "/driver/shipments",
    icon: <FormatListBulletedIcon sx={{ fontSize: "2rem" }} />,
  },
  {
    label: "My Orders",
    route: "/driver/orders",
    icon: <AssignmentIcon sx={{ fontSize: "2rem" }} />,
  },
];

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  onNavigate,
  width = "20rem",
}) => {
  const userData = useUserData();
  const router = useIonRouter();
  const currentRoute = router.routeInfo?.pathname;
  const { demoMode } = useDemoMode();

  const isDriverOnly = userData?.role === "driver";
  const isDealerOnly = userData?.role === "dealer";
  const isManufacturerAdmin = userData?.role === "manufacturer" && userData?.isAdmin;

  const navOptions = React.useMemo(() => {
    const source = isDriverOnly
      ? driverNavOptions
      : isDealerOnly
      ? dealerNavOptions
      : adminNavOptions;
    return source.filter((opt) => {
      if (opt.requiresDemo && !demoMode) return false;
      if (opt.requiresManufacturerAdmin && !(isManufacturerAdmin || userData?.isDev || userData?.role === "developer")) {
        return false;
      }
      return true;
    });
  }, [demoMode, isDealerOnly, isDriverOnly, isManufacturerAdmin, userData?.isDev, userData?.role]);

  const handleClick = (route: string) => {
    router.push(route);
    if (onNavigate) onNavigate(route);
  };

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: "100vh",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        // boxShadow: 3
      }}
    >
      <List disablePadding>
        {navOptions.map((opt, index) => (
          <ListItem key={opt.route} disablePadding>
            <ListItemButton
              onClick={() => handleClick(opt.route)}
              sx={{
                height: "4.25rem",
                borderBottom: 0,
                borderBottomColor: "divider",
                backgroundColor: opt.route === currentRoute ? "primary.main" : "background",
                "&:hover": opt.route === currentRoute ? { backgroundColor: "primary.main" } : undefined,
              }}
            >
              <ListItemIcon
                sx={(theme) => ({
                  "& svg": {
                    color: opt.route === currentRoute ? `${theme.palette.background.default} !important` : theme.palette.primary.main,
                  },
                })}
              >
                {opt.icon}
              </ListItemIcon>
              <ListItemText
                primary={opt.label}
                slotProps={{
                  primary: {
                    fontWeight: "500",
                    fontSize: '1.25rem'
                  },
                }}
                sx={{
                  color: opt.route === currentRoute ? "background.default" : "primary.main",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {userData?.isDev && !isDealerOnly && !isDriverOnly && (
          <ListItem key="/admin/dev-tools" disablePadding>
            <ListItemButton
              onClick={() => handleClick("/admin/dev-tools")}
              sx={{
                height: "4.25rem",
                borderBottom: 0,
                borderBottomColor: "divider",
                backgroundColor: "/admin/dev-tools" === currentRoute ? "primary.main" : "background",
                "&:hover": "/admin/dev-tools" === currentRoute ? { backgroundColor: "primary.main" } : undefined,
              }}
            >
              <ListItemIcon
                sx={(theme) => ({
                  "& svg": {
                    color: "/admin/dev-tools" === currentRoute ? `${theme.palette.background.default} !important` : theme.palette.primary.main,
                  },
                })}
              >
                <BuildCircleIcon sx={{ fontSize: "2rem" }} />
              </ListItemIcon>
              <ListItemText
                primary="Developer Tools"
                slotProps={{
                  primary: {
                    fontWeight: "500",
                    fontSize: '1.25rem'
                  },
                }}
                sx={{
                  color: "/admin/dev-tools" === currentRoute ? "background.default" : "primary.main",
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export default DashboardSidebar;
